"use client";

import { useState } from "react";
import { usePdpVariantNavigation } from "./variant-navigation-context";

import { cn } from "@/lib/utils";

export interface QuantityOption {
	qty: number;
	totalLabel: string;
	perUnitLabel: string;
	savingsLabel?: string;
	/**
	 * Saleor variant id for pack-variant products (where qty == variant).
	 * When present, clicking the option navigates to ?variant=<id>; otherwise the
	 * picker uses internal state and writes a hidden <input name="quantity">.
	 */
	variantId?: string;
}

interface QuantityPickerProps {
	options: QuantityOption[];
	initialQty?: number;
	/** Called when the user picks a qty (only fires in form-input mode). */
	onChange?: (qty: number) => void;
	pieceLabel: string;
	perUnitSuffix: string;
	inputName?: string;
	/**
	 * For pack-variant products, the controller passes the currently-selected
	 * variantId so we know which option to highlight as active. Otherwise
	 * `initialQty` is used.
	 */
	selectedVariantId?: string;
}

const COLS_BY_COUNT: Record<number, string> = {
	1: "grid-cols-1",
	2: "grid-cols-2",
	3: "grid-cols-3",
	4: "grid-cols-4",
};

export function QuantityPicker({
	options,
	initialQty = 1,
	onChange,
	pieceLabel,
	perUnitSuffix,
	inputName = "quantity",
	selectedVariantId,
}: QuantityPickerProps) {
	const navigation = usePdpVariantNavigation();

	// Pack-variant mode: any option carries a variantId → URL-driven, no internal state.
	const isVariantMode = options.some((o) => o.variantId);
	const [selectedQty, setSelectedQty] = useState(initialQty);

	if (isVariantMode && !navigation)
		throw new Error("Variant QuantityPicker requires PdpVariantNavigationProvider");
	const isPending = isVariantMode && (navigation?.pending ?? false);

	const activeQty = isVariantMode ? options.find((o) => o.variantId === selectedVariantId)?.qty : selectedQty;

	const select = (opt: QuantityOption) => {
		if (isVariantMode && opt.variantId) {
			navigation?.selectVariant(opt.variantId);
		} else {
			setSelectedQty(opt.qty);
			onChange?.(opt.qty);
		}
	};

	const colsClass = COLS_BY_COUNT[options.length] ?? "grid-cols-3";

	return (
		<>
			{/* Always render the hidden input (even in variant mode where it's harmless)
			    to keep server/client trees identical — conditional render here was
			    triggering a hydration mismatch. */}
			<input type="hidden" name={inputName} value={activeQty ?? selectedQty ?? 1} />
			<div role="radiogroup" aria-label={pieceLabel} className={cn("grid gap-2", colsClass)}>
				{options.map((opt) => {
					const active = opt.qty === activeQty;
					return (
						<button
							key={opt.variantId ?? opt.qty}
							type="button"
							role="radio"
							aria-checked={active}
							disabled={isPending}
							onClick={() => select(opt)}
							className={cn(
								"relative flex flex-col gap-0.5 rounded-xl border-2 p-3 text-left transition-colors",
								active
									? "border-primary bg-primary text-primary-foreground"
									: "hover:border-primary/40 border-border bg-card text-foreground",
							)}
						>
							<span className="text-xs font-bold">
								{opt.qty} {pieceLabel}
							</span>
							<span className="text-base font-extrabold">{opt.totalLabel}</span>
							<span className={cn("text-[10px]", active ? "opacity-75" : "opacity-60")}>
								{opt.perUnitLabel}
								{perUnitSuffix}
							</span>
							{opt.savingsLabel && (
								<span
									className={cn(
										"absolute right-2 top-2 rounded px-1.5 py-0.5 text-[9px] font-bold",
										active ? "bg-primary-foreground text-primary" : "bg-success/10 text-success",
									)}
								>
									−{opt.savingsLabel}
								</span>
							)}
						</button>
					);
				})}
			</div>
		</>
	);
}
